// Flagged issues (ADR-0031) and agent tailoring reviews (ADR-0028), each on its own page with its full explanation, and the
// Action center's "Resolved / history" list. Everything shown is published by the engine (completion key "issues" = issue.list
// --detail; planning key "tailoring" = program.tailoring): the procedure and its task, the full result, the workpaper and area,
// the risks it responds to, every source resolved (document, trial balance line with its amount, evidence reading with how it
// was confirmed), who proposed and who accepted the work, related questions and requests, how it was addressed and the status
// history. The acts offered are the engine's (`acts`); each is one inbox command through the act drawer, a person's own act.
import { Fragment, useMemo, useState, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useApp } from "../../app/context";
import { Button, Card, EmptyState, ErrorState, IssueStateCount, IssueStatusChip, LoadingRows, PageHeader, StatusPill } from "../../components";
import type { DocModel, WsSource } from "../../data/types";
import { fmtDate, fmtStamp } from "../../lib/format";
import { useLoad, usePoll, useRealtime } from "../../lib/hooks";
import {
  ISSUE_FILTERS, chipKind, chipOf, chipText, filterIssues, isSettledState, issueAreas, issueHref, issuesDoc, stateCounts, tailoringDoc,
  type Issue, type IssueChip, type IssueHistory as HistoryEntry, type IssueLink, type IssueSource, type TailoringReview,
} from "../../lib/issues";
import { settleKey } from "../../lib/settled";
import type { StatusKind } from "../../lib/status";
import { ItemAction, Who, useWs, useWsAct } from "./common";
import { IssueExplainer, RiskList } from "./IssueExplainer";

/** Reload a published document on any workspace change, a finished act, and by short polling (lib/hooks POLL_MS). */
function useLive<T>(fn: () => Promise<T>, deps: unknown[]) {
  const { ds } = useApp();
  const r = useLoad(fn, [ds, ...deps]);
  useRealtime(ds, "workspace", () => r.reload());
  useRealtime(ds, "inbox", (e) => { if (e.row?.status === "done") r.reload(); });
  usePoll(ds, r.reload);
  return r;
}

// ─────────────────────────────────────────────────────────────── the chips of an engagement's issues (chat, Overview)

/** Every issue of an engagement by key → its chip and title (null until published). One read of the completion model, kept live. */
export function useIssueChips(engagementId: string): Map<string, { chip: IssueChip; title: string }> | null {
  const { ds } = useApp();
  const docs = useLive(() => ds.table(engagementId, "completion").catch(() => [] as DocModel[]), [engagementId]);
  return useMemo(() => {
    if (!docs.data) return null;
    const { doc } = issuesDoc(docs.data);
    return new Map((doc?.issues ?? []).map((i) => [i.key, { chip: chipOf(i), title: i.title }]));
  }, [docs.data]);
}

/** The issue a cited record is ("procedure:12" → exc:12, "adjustment:AJE-01" → adj:AJE-01, "deficiency:3" → def:3). */
const REF_KIND: Record<string, string> = { procedure: "exc", adjustment: "adj", deficiency: "def" };
export function issueKeyOfRef(ref: string): string | null {
  const n = ref.indexOf(":");
  if (n < 1 || n === ref.length - 1) return null;
  const kind = ref.slice(0, n), id = ref.slice(n + 1);
  if (kind === "issue") return id;
  return REF_KIND[kind] ? `${REF_KIND[kind]}:${id}` : null;
}

/** In a chat message: each cited record that is a flagged issue, linked to its page, with its status chip. */
export function IssueRefChips({ sources, engagementId, chips }: { sources: WsSource[]; engagementId: string; chips: Map<string, { chip: IssueChip; title: string }> | null }) {
  if (!chips) return null;
  const keys = [...new Set(sources.map((s) => issueKeyOfRef(s.ref)).filter((k): k is string => !!k && chips.has(k)))];
  if (!keys.length) return null;
  return (
    <ul className="flex flex-wrap gap-1.5" aria-label="Flagged issues cited">
      {keys.map((k) => (
        <li key={k} className="flex items-center gap-1 text-[11px]">
          <Link className="text-brand hover:underline" to={issueHref(engagementId, k)}>{chips.get(k)!.title}</Link>
          <IssueStatusChip chip={chips.get(k)!.chip} />
        </li>
      ))}
    </ul>
  );
}

/** Overview: the engagement's flagged issues counted by the status every screen shows, each count leading to the history filtered. */
export function FlaggedIssuesSummary({ engagementId }: { engagementId: string }) {
  const chips = useIssueChips(engagementId);
  if (!chips || chips.size === 0) return null;
  const counts = stateCounts([...chips.values()].map((x) => x.chip));
  const filterOf = (state: string) => ISSUE_FILTERS.find((f) => f.id !== "all" && f.id !== "updated" && f.test({ state } as IssueChip))?.id ?? "all";
  return (
    <section aria-labelledby="flagged-h" className="space-y-2 rounded-lg border border-line bg-raised px-4 py-3">
      <header className="flex items-center justify-between">
        <h2 id="flagged-h" className="text-sm font-semibold">Flagged issues · {chips.size}</h2>
        <Link to={`/e/${engagementId}/actions/history`} className="text-xs text-brand hover:underline">Resolved / history</Link>
      </header>
      <ul className="flex flex-wrap gap-1.5" aria-label="Flagged issues by status">
        {counts.map((c) => <li key={c.state}><Link to={`/e/${engagementId}/actions/history?filter=${filterOf(c.state)}`}><IssueStateCount state={c.state} count={c.count} /></Link></li>)}
      </ul>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────── history list (Action center tab)

export function IssueHistory({ engagements, names, byClient }: { engagements: string[]; names: Map<string, string>; byClient: boolean }) {
  const { ds } = useApp();
  const [params] = useSearchParams();
  const [status, setStatus] = useState(() => (ISSUE_FILTERS.some((f) => f.id === params.get("filter")) ? params.get("filter")! : "all"));
  const [area, setArea] = useState("all");
  const key = engagements.join("|");
  const docs = useLive(async () => Promise.all(engagements.map(async (id) => {
    const rows = await ds.table(id, "completion").catch(() => [] as DocModel[]);
    return { id, ...issuesDoc(rows) };
  })), [key]);
  if (docs.error) return <ErrorState error={docs.error} onRetry={docs.reload} />;
  if (!docs.data) return <LoadingRows rows={4} label="Loading the issue history" />;
  const all = docs.data.flatMap((d) => (d.doc?.issues ?? []).map((i) => ({ ...i, engagementId: d.id })));
  const failed = docs.data.filter((d) => d.error);
  const unpublished = docs.data.filter((d) => !d.doc && !d.error);
  const rows = filterIssues(all, status, area) as Array<Issue & { engagementId: string }>;
  const count = (id: string) => filterIssues(all, id, area).length;
  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold">Resolved / history</h2>
      <p className="text-sm text-muted">Every flagged issue with its status and how it was addressed, updated or closed. Open one for the full explanation and its history.</p>
      <div className="flex flex-wrap items-center gap-3">
        <div role="group" aria-label="Status" className="flex flex-wrap gap-1.5">
          {ISSUE_FILTERS.map((s) => (
            <button key={s.id} type="button" aria-pressed={status === s.id} onClick={() => setStatus(s.id)}
              className={`rounded-full border px-2.5 py-1 text-xs ${status === s.id ? "border-brand bg-brand/10 font-semibold text-brand" : "border-line bg-raised hover:bg-sunken"}`}>
              {s.label} <span className="tabular-nums text-muted">{count(s.id)}</span>
            </button>
          ))}
        </div>
        <label className="flex items-center gap-1.5 text-xs text-muted">Area
          <select aria-label="Area" className="rounded-md border border-line bg-paper px-2 py-1 text-xs text-ink" value={area} onChange={(e) => setArea(e.target.value)}>
            <option value="all">All areas</option>
            {issueAreas(all).map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
      </div>
      {failed.map((d) => <ErrorState key={d.id} error={`${names.get(d.id) ?? d.id}: the issues could not be published — ${d.error}`} />)}
      {unpublished.length > 0 && all.length === 0 && failed.length === 0
        ? <EmptyState title="The engine has not published the issue history yet.">It appears after the engine's next publish of this engagement.</EmptyState>
        : rows.length === 0 ? <EmptyState title="No issues match.">Change the status or area filter.</EmptyState> : (
          <div className="overflow-x-auto rounded-lg border border-line bg-raised">
            <table className="w-full min-w-[720px] text-sm">
              <caption className="sr-only">Flagged issues and how they were addressed</caption>
              <thead className="bg-sunken text-left text-xs text-muted"><tr>
                <th className="w-[32%] px-3 py-1.5 font-medium">Issue</th>{byClient && <th className="px-3 py-1.5 font-medium">Client</th>}<th className="w-32 px-3 py-1.5 font-medium">Area</th>
                <th className="w-40 px-3 py-1.5 font-medium">Status</th><th className="w-[34%] px-3 py-1.5 font-medium">How it was addressed / latest</th>
              </tr></thead>
              <tbody className="divide-y divide-line">
                {rows.map((i) => (
                  <tr key={`${i.engagementId}:${i.key}`} className="align-top">
                    <td className="px-3 py-2"><Link to={issueHref(i.engagementId, i.key)} className="font-medium text-brand hover:underline">{i.title}</Link>
                      <p className="text-xs text-muted">{i.kind_label}{i.workpaper ? ` · ${i.workpaper.ref ?? i.workpaper.id}` : ""}</p>
                      <p className="line-clamp-2 text-xs text-muted">{i.summary}</p></td>
                    {byClient && <td className="px-3 py-2 text-xs">{names.get(i.engagementId) ?? i.engagementId}</td>}
                    <td className="px-3 py-2 text-xs">{i.area ?? "General"}</td>
                    <td className="px-3 py-2"><IssueStatusChip issue={i} /></td>
                    <td className="px-3 py-2 text-xs">{i.resolution?.how ?? i.last_note ?? <span className="text-muted">—</span>}{(i.resolution?.by ?? i.last_by) && <span className="mt-0.5 block text-muted">— {i.resolution?.by ?? i.last_by}{(i.resolution?.at ?? i.last_at) ? `, ${fmtDate(i.resolution?.at ?? i.last_at)}` : ""}</span>}
                      {i.updated && i.resolution && <span className="mt-0.5 block text-progress">Updated {fmtDate(i.updated.at)}: {i.updated.note}</span>}
                      {i.reopened && !i.resolution && <span className="mt-0.5 block text-muted">Reopened {fmtDate(i.reopened.at)} by {i.reopened.by}: {i.reopened.reason}</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────── one issue

export function IssuePage() {
  const { key: raw = "" } = useParams();
  const key = decodeURIComponent(raw);
  const ws = useWs();
  const { ds } = useApp();
  const docs = useLive(() => ds.table(ws.id, "completion"), [ws.id]);
  const { open, drawer } = useWsAct(docs.reload);
  if (docs.error) return <ErrorState error={docs.error} onRetry={docs.reload} />;
  if (!docs.data) return <LoadingRows rows={6} label="Loading the issue" />;
  const { doc, error } = issuesDoc(docs.data);
  if (error) return <ErrorState error={`The engine could not publish the issues: ${error}`} onRetry={docs.reload} />;
  const i = doc?.issues.find((x) => x.key === key);
  const back = <Link className="text-brand hover:underline" to={`/e/${ws.id}/actions/issue`}>Flagged issues</Link>;
  if (!doc) return <EmptyState title="The engine has not published the issue details yet.">They appear after its next publish of this engagement. {back}</EmptyState>;
  if (!i) return <EmptyState title={`No issue ${key} in this engagement.`}>{back} · <Link className="text-brand hover:underline" to={`/e/${ws.id}/actions/history`}>Resolved / history</Link></EmptyState>;
  const p = i.procedure;
  const actBtn = (which: "update" | "resolve" | "reopen" | "info" | "point_add", kind: "primary" | "secondary") => {
    const a = i.acts[which];
    if (!a?.command) return null;
    return (
      <Button key={which} kind={kind} size="sm" data-command={a.command}
        onClick={() => open({ command: a.command!, engagementId: ws.id, fixed: a.payload, title: `${a.label}: ${i.title}`,
          settles: which === "resolve" ? settleKey(ws.id, `iss:${i.key}`) : undefined })}>{a.label}</Button>
    );
  };
  const askRef = i.workpaper?.ref;
  const ask = (
    <Button size="sm" data-command="wp.message"
      onClick={() => open({ command: "wp.message", engagementId: ws.id, title: `Ask about: ${i.title}`,
        fixed: { scope: askRef ? "workpaper" : "engagement", ...(askRef ? { ref: askRef } : {}), kind: "question", ...(p ? { sources: [`procedure:${p.id}`] } : {}) },
        initial: { to: "agent", subkind: "clarification" } })}>Ask about this issue</Button>
  );
  return (
    <article className="space-y-4" aria-labelledby="issue-title">
      <PageHeader
        crumbs={<nav aria-label="Breadcrumb" className="text-xs text-muted"><Link className="hover:underline" to={`/e/${ws.id}/actions/issue`}>Action center</Link> › {back} › {i.kind_label}</nav>}
        title={<span id="issue-title">{i.title}</span>}
        subtitle={<span className="block space-y-1">
          <span className="flex flex-wrap items-center gap-2"><IssueStatusChip issue={i} /><span>{i.kind_label}{i.area ? ` · ${i.area}` : ""}{i.created_at ? ` · recorded ${fmtDate(i.created_at)}` : ""}</span></span>
          {i.workpaper && <span className="block">Workpaper <Link className="text-brand hover:underline" to={`/e/${ws.id}/wp/${encodeURIComponent(i.workpaper.ref ?? i.workpaper.id)}`}>{i.workpaper.ref ?? i.workpaper.id} {i.workpaper.name}</Link></span>}
        </span>}
        actions={<div className="flex flex-wrap gap-1.5">{actBtn("resolve", "primary")}{actBtn("update", "secondary")}{actBtn("info", "secondary")}{actBtn("point_add", "secondary")}{actBtn("reopen", "secondary")}{ask}</div>} />
      {i.acts.resolve && !i.acts.resolve.command && i.acts.resolve.why && <p className="text-sm text-muted">This issue is {i.acts.resolve.why}.</p>}

      <StatusBanner issue={i} engagementId={ws.id} />

      {(i.points ?? []).length > 0 && <PointsCard issue={i} open={open} engagementId={ws.id} />}

      <IssueExplainer issue={i} open={open} />

      <Original issue={i}>
        {p && (
          <dl className="space-y-3 text-sm">
            <Row label="Procedure">{p.task ? <span className="mr-1.5 font-mono text-xs text-muted">{p.task}</span> : null}<span className="whitespace-pre-wrap">{p.text}</span>
              <span className="block text-xs text-muted">{p.workpaper}, block {p.block}, row {p.row} · procedure {p.id}</span></Row>
            <Row label="Result"><span className="whitespace-pre-wrap">{i.original?.text ?? p.result ?? "—"}</span>
              {i.original?.changed && i.original.now && <span className="mt-1 block text-xs">Recorded now: <span className="whitespace-pre-wrap">{i.original.now}</span></span>}</Row>
            <Row label="Outcome"><StatusPill state={p.status === "exception" ? "blocked" : p.status} label={p.status === "exception" ? "Exception" : p.status} />
              {p.performed_by && <span className="ml-2 text-xs text-muted">preparer of record {p.performed_by}{p.performed_at ? `, ${fmtStamp(p.performed_at)}` : ""}</span>}</Row>
          </dl>
        )}
        {!p && <p className="whitespace-pre-wrap text-sm">{i.original?.text ?? i.summary}</p>}
        {i.record && <div className="mt-3"><RecordCard issue={i} /></div>}
      </Original>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Who did the work" className="min-w-0">
          <dl className="space-y-2 text-sm">
            <Row label="Proposed">{i.proposed ? <>{i.proposed.agent ? <>drafted by <span className="font-mono text-xs">{i.proposed.agent}</span> for </> : "by "}<Who name={i.proposed.by} />{i.proposed.at ? `, ${fmtStamp(i.proposed.at)}` : ""}</> : <span className="text-muted">recorded directly by a person</span>}</Row>
            <Row label="Accepted">{i.accepted ? <><Who name={i.accepted.by} />, {fmtStamp(i.accepted.at)}{i.accepted.channel ? " (from the web app)" : ""}</> : <span className="text-muted">{i.proposed?.agent ? "not accepted yet" : "—"}</span>}</Row>
          </dl>
        </Card>
        <Card title="Risks it responds to" className="min-w-0">
          {!i.risks ? <p className="text-sm text-muted">Not applicable to this kind of issue.</p>
            : i.risks.risks.length === 0 && (i.risks.area_risks ?? []).length > 0 ? (
              <div className="space-y-2">
                <p className="text-xs text-muted">Area risks — {i.risks.why}.</p>
                <RiskList risks={i.risks.area_risks!} label="Area risks" />
              </div>
            )
            : i.risks.risks.length === 0 ? <p className="text-sm text-muted">None linked: {i.risks.why}.</p> : (
              <ul className="space-y-2 text-sm">
                {i.risks.risks.map((r) => (
                  <li key={r.id}><span className="font-medium">{r.area} · {r.assertion}</span> — IR {r.inherent}, CR {r.control}, RMM {r.rmm}{r.significant ? " · significant" : ""}{r.fraud ? " · fraud" : ""}
                    <span className="block text-xs text-muted">{r.rationale} — {r.by}, {fmtDate(r.at)} (risk record {r.id})</span></li>
                ))}
                {i.risks.assertions.length > 0 && <li className="text-xs text-muted">The step addresses {i.risks.assertions.join(", ")}.</li>}
              </ul>
            )}
        </Card>
      </div>

      <Card title={`Sources cited${i.sources.length ? ` · ${i.sources.length}` : ""}`}>
        {i.sources.length === 0 ? <p className="text-sm text-muted">No sources are cited on this record.</p> : <SourceList sources={i.sources} engagementId={ws.id} />}
      </Card>

      {i.flags.length > 0 && (
        <Card title="Changes that reached this procedure">
          <ul className="space-y-1.5 text-sm">{i.flags.map((f) => <li key={f.id}><StatusPill state={f.status === "open" ? "stale" : "done"} label={f.status === "open" ? "Open flag" : "Cleared"} /> {f.why}
            {f.cleared_how && <span className="block text-xs text-muted">Cleared by {f.cleared_by}: {f.cleared_how}</span>}</li>)}</ul>
        </Card>
      )}

      <Card title="Questions and requests about it">
        <Related issue={i} open={open} />
      </Card>

      <Card title="Status history">
        <History entries={i.history} engagementId={ws.id} />
      </Card>
      {drawer}
    </article>
  );
}

const BANNER: Record<string, string> = {
  done: "border-done-line bg-done-bg", progress: "border-progress-line bg-progress-bg", waiting: "border-waiting-line bg-waiting-bg",
  blocked: "border-blocked-line bg-blocked-bg", neutral: "border-neutral-line bg-neutral-bg",
};

/** ADR-0034: the status at the top of the issue page — its chip, how it was settled (or what is new, or how far its points are), by whom,
 * when, and the evidence. Open and never worked: nothing to say beyond the chip in the header. */
function StatusBanner({ issue: i, engagementId }: { issue: Issue; engagementId: string }) {
  const c = chipOf(i);
  const r = i.resolution;
  const pts = c.points;
  if (c.state === "open" && !i.reopened && !pts) return null;
  const heading = r ? (i.status === "closed" ? "How it was closed" : "How it was addressed") : c.state === "updated" ? "What is new" : pts ? "Where its points stand"
    : i.reopened && c.state === "open" ? "Reopened" : "Being addressed";
  return (
    <section aria-label="Status of this issue" data-issue-state={c.state} className={`space-y-2 rounded-lg border px-4 py-3 text-sm ${BANNER[chipKind(c)]}`}>
      <p className="flex flex-wrap items-center gap-2"><IssueStatusChip chip={c} /><span className="font-semibold">{heading}</span></p>
      {r && (
        <div className="space-y-1.5">
          <p className="whitespace-pre-wrap">{r.how ?? "—"}</p>
          <p className="text-xs text-muted">{r.by ? <>{chipText(c)} by <Who name={r.by} />{r.agent ? ` (recorded by ${r.agent} on their instruction)` : ""}{r.at ? `, ${fmtStamp(r.at)}` : ""}</> : "Closed by its own record."}</p>
          {r.instruction && <p className="text-xs text-muted">Instruction, word for word: “{r.instruction}”</p>}
          {r.links.length > 0 && <Links links={r.links} engagementId={engagementId} />}
          {r.sources.length > 0 && <SourceList sources={r.sources} engagementId={engagementId} />}
        </div>
      )}
      {i.updated && (
        <div className="space-y-1.5">
          <p><span className="font-medium">New information{r ? " since it was settled" : ""}:</span> <span className="whitespace-pre-wrap">{i.updated.note}</span></p>
          <p className="text-xs text-muted">Recorded by <Who name={i.updated.by} />{i.updated.agent ? ` (drafted by ${i.updated.agent})` : ""}, {fmtStamp(i.updated.at)} — shown as updated until the next status act.</p>
          {i.updated.links.length > 0 && <Links links={i.updated.links} engagementId={engagementId} />}
          {i.updated.sources.length > 0 && <SourceList sources={i.updated.sources} engagementId={engagementId} />}
        </div>
      )}
      {!r && pts && <p>{pts.addressed} of {pts.total} point{pts.total === 1 ? "" : "s"} addressed — each point below has its own status and history.</p>}
      {!r && !i.updated && i.last_note && i.status !== "open" && (
        <p><span className="font-medium">Latest:</span> {i.last_note} <span className="text-muted">— {i.last_by}{i.last_at ? `, ${fmtStamp(i.last_at)}` : ""}</span></p>
      )}
      {i.reopened && !r && <p className="text-xs"><span className="font-medium">Reopened</span> by <Who name={i.reopened.by} />, {fmtStamp(i.reopened.at)}: {i.reopened.reason}</p>}
    </section>
  );
}

/** The flag as first recorded, never rewritten: dimmed and labelled once the issue is settled, updated or partly addressed. */
function Original({ issue: i, children }: { issue: Issue; children: ReactNode }) {
  const c = chipOf(i);
  const dim = isSettledState(c.state) || c.state === "updated" || c.state === "partly_addressed" || !!i.reopened;
  const at = i.original?.at ?? i.first_flagged_at ?? i.created_at;
  const label = at ? `As first flagged on ${fmtDate(at)}` : "As first flagged";
  return (
    <section aria-label="What was found" data-dimmed={dim ? "true" : "false"} className={`rounded-lg border border-line bg-raised ${dim ? "opacity-70" : ""}`}>
      <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line px-4 py-2.5">
        <h2 className="text-sm font-semibold">What was found</h2>
        <span className="text-xs text-muted">{dim ? `${label} — kept as it was flagged` : label}</span>
      </header>
      <div className="px-4 py-3">{children}</div>
    </section>
  );
}

/** The points of an issue (ADR-0034), each with its own status, proposal, resolution, history and the acts the engine offers. */
function PointsCard({ issue: i, open, engagementId }: { issue: Issue; open: Parameters<typeof ItemAction>[0]["open"]; engagementId: string }) {
  const pts = i.points ?? [];
  const c = chipOf(i);
  return (
    <Card title={`Points · ${c.points ? `${c.points.addressed} of ${c.points.total} addressed` : pts.length}`}>
      <ol className="space-y-3" aria-label="Points of this issue">
        {pts.map((p) => (
          <li key={p.id ?? `original-${p.no}`} data-point-status={p.status} className={`rounded-md border px-3 py-2 text-sm ${p.settled ? "border-done-line" : "border-line"}`}>
            <p className="flex flex-wrap items-center gap-1.5">
              <span className="font-mono text-xs text-muted">{p.no}.</span>
              <StatusPill kind={({ red: "blocked", amber: "waiting", green: "done", grey: "neutral" } as Record<string, StatusKind>)[p.colour] ?? "neutral"} label={p.status_label} />
              {p.added_later && <span className="rounded border border-progress-line bg-progress-bg px-1.5 py-0.5 text-[11px] font-medium text-progress">Added later</span>}
              {p.original && <span className="text-xs text-muted">As first flagged</span>}
            </p>
            <p className={`mt-1 whitespace-pre-wrap ${p.settled ? "text-muted" : ""}`}>{p.original ? (i.original?.text ?? i.summary) : p.text}</p>
            {p.added_by && <p className="text-[11px] text-muted">{p.added_later ? "Added later" : "Added"} by <Who name={p.added_by} />{p.agent ? ` (drafted by ${p.agent})` : ""}{p.added_at ? `, ${fmtStamp(p.added_at)}` : ""}</p>}
            {p.proposal && (
              <div className="mt-1.5 rounded border border-waiting-line bg-waiting-bg px-2 py-1.5 text-xs">
                <p><span className="font-medium">Proposed {p.proposal.outcome === "not_an_issue" ? "as not an issue" : "resolution"}:</span> {p.proposal.how}</p>
                <p className="text-muted">by <Who name={p.proposal.by} />{p.proposal.agent ? ` (drafted by ${p.proposal.agent})` : ""}, {fmtStamp(p.proposal.at)} — waits for a person to accept it</p>
                {p.proposal.sources.length > 0 && <div className="mt-1"><SourceList sources={p.proposal.sources} engagementId={engagementId} /></div>}
              </div>
            )}
            {p.resolution && (
              <div className="mt-1.5 text-xs">
                <p><span className="font-medium">{p.resolution.closed_as ? "Not an issue" : "How it was addressed"}:</span> {p.resolution.how}</p>
                <p className="text-muted">by <Who name={p.resolution.by} />{p.resolution.agent ? ` (recorded by ${p.resolution.agent} on their instruction)` : ""}, {fmtStamp(p.resolution.at)}</p>
                {p.resolution.sources.length > 0 && <div className="mt-1"><SourceList sources={p.resolution.sources} engagementId={engagementId} /></div>}
              </div>
            )}
            {p.history.length > 1 && (
              <details className="mt-1.5 text-xs"><summary className="cursor-pointer text-muted">History of this point · {p.history.length}</summary>
                <ol className="mt-1 space-y-1">{p.history.map((h, n) => <li key={n}><span className="text-muted">{h.at ? fmtStamp(h.at) : "—"} · {h.by ?? "—"}{h.agent ? ` (${h.agent})` : ""}</span> — {h.text}</li>)}</ol>
              </details>
            )}
            {Object.keys(p.acts).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(["resolve", "propose", "update"] as const).map((w) => {
                  const a = p.acts[w];
                  if (!a?.command) return null;
                  return <Button key={w} size="sm" kind={w === "resolve" ? "primary" : "secondary"} data-command={a.command}
                    onClick={() => open({ command: a.command!, engagementId, fixed: a.payload, title: `${a.label}: point ${p.no} of ${i.title}` })}>{a.label}</Button>;
                })}
              </div>
            )}
          </li>
        ))}
      </ol>
    </Card>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return <div className="grid gap-1 sm:grid-cols-[8rem_1fr]"><dt className="text-xs font-medium uppercase tracking-wide text-muted">{label}</dt><dd className="min-w-0">{children}</dd></div>;
}

function RecordCard({ issue }: { issue: Issue }) {
  const r = issue.record as Record<string, unknown>;
  const skip = new Set(["kind", "lines"]);
  const lines = Array.isArray(r.lines) ? (r.lines as Array<Record<string, string | null>>) : [];
  return (
    <Card title={`The ${String(r.kind ?? "record")}`}>
      <dl className="space-y-2 text-sm">
        {Object.entries(r).filter(([k, v]) => !skip.has(k) && v !== null && v !== "").map(([k, v]) => (
          <Row key={k} label={k.replace(/_/g, " ")}><span className="whitespace-pre-wrap">{typeof v === "boolean" ? (v ? "yes" : "no") : String(v)}</span></Row>
        ))}
      </dl>
      {lines.length > 0 && (
        <table className="mt-3 w-full text-sm"><caption className="sr-only">Entry lines</caption>
          <thead className="text-left text-xs text-muted"><tr><th className="py-1 font-medium">Account</th><th className="py-1 text-right font-medium">Debit</th><th className="py-1 text-right font-medium">Credit</th></tr></thead>
          <tbody className="divide-y divide-line">{lines.map((l, n) => <tr key={n}><td className="py-1">{l.account} {l.name ?? ""}</td><td className="py-1 text-right font-mono">{l.debit}</td><td className="py-1 text-right font-mono">{l.credit}</td></tr>)}</tbody>
        </table>
      )}
    </Card>
  );
}

/** Each source resolved to what a reviewer reads, with a way to the document in the vault. */
export function SourceList({ sources, engagementId }: { sources: IssueSource[]; engagementId: string }) {
  return (
    <ul className="space-y-2 text-sm" aria-label="Sources">
      {sources.map((s, n) => (
        <li key={n} className={`rounded-md border px-3 py-2 ${s.ok ? "border-line" : "border-blocked-line bg-blocked-bg"}`}>
          {s.reading ? (
            <p><span className="font-medium">{s.reading.label}</span>{s.reading.value !== null && <>: <span className="font-mono">{s.reading.value}</span></>}
              <span className="block text-xs text-muted">{s.doc?.name}{s.reading.page ? `, page ${s.reading.page}` : ""}{s.reading.locator ? ` — ${s.reading.locator}` : ""} · read by {s.reading.read_by} · {s.reading.confirmation}</span></p>
          ) : s.account ? (
            <p><span className="font-medium">Trial balance {s.account.code} {s.account.name}</span>{s.account.balance_text && <>: <span className="font-mono">{s.account.balance_text}</span></>}
              <span className="block text-xs text-muted">{s.account.period === "py" ? "prior year" : "current year"} · {s.describe}</span></p>
          ) : s.doc ? (
            <p><span className="font-medium">{s.doc.name}</span><span className="block text-xs text-muted">{s.describe}</span></p>
          ) : (
            <p>{s.describe ?? s.cite}{s.amount_text && <> — <span className="font-mono">{s.amount_text}</span></>}</p>
          )}
          {!s.ok && <p className="text-xs text-blocked">Does not resolve now: {s.why}</p>}
          <p className="mt-0.5 flex flex-wrap gap-x-3 text-[11px] text-muted"><span className="font-mono">{s.cite}</span>
            {s.doc && <Link className="text-brand hover:underline" to={`/e/${engagementId}/details/evidence?doc=${s.doc.sha256}`}>Open in Evidence</Link>}
            {s.procedure && <span>{s.procedure.workpaper} {s.procedure.task ?? ""} ({s.procedure.status})</span>}</p>
        </li>
      ))}
    </ul>
  );
}

function Links({ links, engagementId }: { links: IssueLink[]; engagementId: string }) {
  const href = (l: IssueLink) => (l.kind === "adjustment" || l.kind === "deficiency" ? issueHref(engagementId, `${l.kind === "adjustment" ? "adj" : "def"}:${l.id}`) : null);
  return (
    <ul className="space-y-1 text-sm" aria-label="Linked records">
      {links.map((l) => {
        const to = href(l);
        return <li key={l.ref}><span className="font-mono text-xs text-muted">{l.ref}</span> {to ? <Link className="text-brand hover:underline" to={to}>{l.label ?? l.ref}</Link> : l.label}
          {l.state && <span className="text-xs text-muted"> ({l.state})</span>}{l.why && <span className="block text-xs text-blocked">{l.why}</span>}</li>;
      })}
    </ul>
  );
}

function Related({ issue, open }: { issue: Issue; open: Parameters<typeof ItemAction>[0]["open"] }) {
  const ws = useWs();
  const { threads, requests, messages } = issue.related;
  if (!threads.length && !requests.length && !messages.length) {
    return <p className="text-sm text-muted">None yet. Use “Ask about this issue” to ask the agent or a colleague; the question and its answer show here and in the chat.</p>;
  }
  const top = messages.filter((m) => !m.parent || !messages.some((x) => x.id === m.parent));
  return (
    <div className="space-y-3 text-sm">
      {top.length > 0 && (
        <ol className="space-y-2" aria-label="Team questions">
          {top.map((m) => {
            const accept = ws.attention.find((a) => a.key === `q:${m.id}` && a.action.command === "wp.accept");
            const answers = messages.filter((x) => x.parent === m.id);
            return (
              <li key={m.id} className="rounded-md border border-line px-3 py-2">
                <p className="text-[11px] text-muted"><Who name={m.author} />{m.agent ? ` (${m.agent})` : ""} · {m.kind}{m.to_person ? ` to ${m.to_person}` : ""} · {fmtStamp(m.at)}
                  {m.status && <> · <StatusPill state={m.status} label={m.status === "answered" ? "Answered — waiting for acceptance" : m.status} /></>}</p>
                <p className="whitespace-pre-wrap">{m.body}</p>
                {answers.map((a) => (
                  <div key={a.id} className="ml-3 mt-2 border-l border-line pl-3">
                    <p className="text-[11px] text-muted">{a.agent ? <span className="text-agent">✦ Agent ({a.agent})</span> : <Who name={a.author} />} · answer · {fmtStamp(a.at)}</p>
                    <p className="whitespace-pre-wrap">{a.body}</p>
                  </div>
                ))}
                {accept && <div className="mt-2"><ItemAction item={accept} open={open} /></div>}
              </li>
            );
          })}
        </ol>
      )}
      {threads.length > 0 && (
        <div><h3 className="text-xs font-semibold text-muted">Client threads</h3>
          <ul className="mt-1 space-y-1">{threads.map((t) => <li key={t.id}><Link className="text-brand hover:underline" to={`/e/${ws.id}/documents`}>{t.subject}</Link> <span className="text-xs text-muted">({t.kind}, {t.status})</span></li>)}</ul></div>
      )}
      {requests.length > 0 && (
        <div><h3 className="text-xs font-semibold text-muted">Client requests</h3>
          <ul className="mt-1 space-y-1">{requests.map((r) => <li key={r.id}><Link className="text-brand hover:underline" to={`/e/${ws.id}/documents#pbc-${r.id}`}>PBC-{r.id} {r.text}</Link> <span className="text-xs text-muted">({r.status}{r.due ? `, due ${fmtDate(r.due)}` : ""})</span></li>)}</ul></div>
      )}
    </div>
  );
}

function History({ entries, engagementId }: { entries: HistoryEntry[]; engagementId: string }) {
  if (!entries.length) return <p className="text-sm text-muted">No changes recorded yet.</p>;
  return (
    <ol className="space-y-3 border-l border-line pl-4 text-sm" aria-label="Status history">
      {entries.map((h, n) => (
        <li key={n} className="relative">
          <span aria-hidden="true" className={`absolute -left-[1.4rem] top-1.5 h-2.5 w-2.5 rounded-full ${h.kind === "status" ? "bg-brand" : "bg-line"}`} />
          <p className="text-[11px] text-muted">{h.at ? fmtStamp(h.at) : "date not recorded"} · {h.by ? <Who name={h.by} /> : "—"}{h.agent ? ` (by ${h.agent})` : ""}{h.channel ? " · from the web app" : ""}{h.command ? ` · ${h.command}` : ""}</p>
          <p className="whitespace-pre-wrap">{h.reopen ? <StatusPill kind="blocked" label="Reopened" />
            : (h.kind === "status" || h.kind === "info") && h.status ? <IssueStatusChip issue={{ status: h.status, closed_as: h.closed_as, state: h.state ?? (h.kind === "info" ? "updated" : null) }} />
            : h.kind === "point" ? <StatusPill kind="neutral" label="Point" /> : null} {h.text}</p>
          {h.instruction && <p className="text-xs text-muted">Instruction, word for word: “{h.instruction}”</p>}
          {h.links.length > 0 && <div className="mt-1"><Links links={h.links} engagementId={engagementId} /></div>}
          {h.sources.length > 0 && <div className="mt-1"><SourceList sources={h.sources} engagementId={engagementId} /></div>}
        </li>
      ))}
    </ol>
  );
}

// ─────────────────────────────────────────────────────────────── an agent's tailoring, for the manager's review

export function TailoringPage() {
  const { wp: raw = "" } = useParams();
  const wpId = decodeURIComponent(raw);
  const ws = useWs();
  const { ds } = useApp();
  const docs = useLive(() => ds.table(ws.id, "planning"), [ws.id]);
  const { open, drawer } = useWsAct(docs.reload);
  if (docs.error) return <ErrorState error={docs.error} onRetry={docs.reload} />;
  if (!docs.data) return <LoadingRows rows={6} label="Loading the tailoring review" />;
  const { reviews, error } = tailoringDoc(docs.data);
  if (error) return <ErrorState error={`The engine could not publish the tailoring reviews: ${error}`} onRetry={docs.reload} />;
  const t = reviews?.find((x) => x.workpaper === wpId);
  const w = ws.wp.find((x) => x.workpaperId === wpId);
  const back = <Link className="text-brand hover:underline" to={`/e/${ws.id}/actions/approval`}>Your approvals</Link>;
  if (!reviews) return <EmptyState title="The engine has not published the tailoring reviews yet.">They appear after its next publish of this engagement. {back}</EmptyState>;
  if (!t) return <EmptyState title={`No agent tailoring of ${wpId} to review.`}>{back}</EmptyState>;
  return (
    <article className="space-y-4" aria-labelledby="tail-title">
      <PageHeader
        crumbs={<nav aria-label="Breadcrumb" className="text-xs text-muted"><Link className="hover:underline" to={`/e/${ws.id}/actions/approval`}>Action center</Link> › {back} › Tailoring review</nav>}
        title={<span id="tail-title">Review the agent's tailoring of {w ? `${w.ref} ${w.name}` : wpId}</span>}
        subtitle={<span className="flex flex-wrap items-center gap-2"><StatusPill kind={t.status === "pending" ? "waiting" : "done"} label={t.status === "pending" ? "Waiting for your review" : t.status === "approved" ? "Approved" : t.status === "reverted" ? "Reverted" : t.status} />
          <span>Tailored by {t.tailoring.agents.join(", ")} for {t.tailoring.by.join(", ")}, {fmtStamp(t.tailoring.at)} (audit seq {t.tailoring.seq.join(", ")})</span>
          {w && <Link className="text-brand hover:underline" to={`/e/${ws.id}/wp/${encodeURIComponent(w.ref)}`}>Open the program</Link>}</span>}
        actions={<TailoringActs t={t} open={open} engagementId={ws.id} />} />
      <p className="text-sm text-muted">{t.tailoring.policy}. Approving keeps the tailoring as your own; reverting reopens every procedure it set not applicable.</p>
      {t.review && (
        <Card title="The review" className="border-done-line">
          <p className="text-sm">{t.review.decision === "approve" ? "Approved" : "Reverted"} by <Who name={t.review.by} />, {fmtStamp(t.review.at)} (audit seq {t.review.seq}).{t.review.note ? ` “${t.review.note}”` : ""}
            {t.review.reopened.length > 0 && ` Reopened procedures ${t.review.reopened.join(", ")}.`}</p>
        </Card>
      )}
      <Card title={`Risk records it rests on · ${t.risks.length}`}>
        {t.risks.length === 0 ? <p className="text-sm text-muted">No risk records for {t.areas.join(", ")}.</p> : (
          <div className="overflow-x-auto"><table className="w-full min-w-[640px] text-sm"><caption className="sr-only">Risk records</caption>
            <thead className="whitespace-nowrap text-left text-xs text-muted"><tr><th className="py-1 pr-3 font-medium">Risk</th><th className="py-1 pr-3 font-medium">Area · assertion</th><th className="py-1 pr-3 font-medium">IR / CR / RMM</th>
              <th className="py-1 pr-3 font-medium">Significant · fraud</th><th className="py-1 font-medium">Rationale</th></tr></thead>
            <tbody className="divide-y divide-line">{t.risks.map((r) => (
              <tr key={r.id} className={`align-top ${r.cited ? "" : "text-muted"}`}>
                <td className="whitespace-nowrap py-1.5 pr-3 font-mono text-xs">{r.id}{r.cited && <span className="ml-1 rounded bg-brand/10 px-1 text-[10px] text-brand">cited</span>}{!r.current && <span className="ml-1 text-[10px]">superseded</span>}</td>
                <td className="whitespace-nowrap py-1.5 pr-3">{r.area} · {r.assertion}</td><td className="whitespace-nowrap py-1.5 pr-3 font-mono">{r.inherent} / {r.control} / {r.rmm}</td>
                <td className="whitespace-nowrap py-1.5 pr-3">{r.significant ? "significant" : "—"} · {r.fraud ? "fraud" : "—"}</td>
                <td className="py-1.5 text-xs">{r.rationale}<span className="block text-muted">{r.by}{r.agent ? ` (${r.agent})` : ""}, {fmtDate(r.at)}</span></td>
              </tr>))}</tbody></table></div>
        )}
      </Card>
      <Card title={`Procedures set not applicable · ${t.not_applicable.length}`}>
        {t.not_applicable.length === 0 ? <p className="text-sm text-muted">The tailoring set no procedure not applicable.</p> : (
          <ol className="divide-y divide-line text-sm">{t.not_applicable.map((p) => (
            <li key={p.id} className="py-2"><p><span className="mr-1.5 font-mono text-xs text-muted">{p.task}</span><span className="whitespace-pre-wrap">{p.text}</span></p>
              <p className="mt-0.5 text-xs text-muted">Reason: {p.na_reason ?? "—"}</p>
              <p className="text-xs text-muted">Now: {p.status === "na" ? "not applicable" : p.status}{(p.risk_ids ?? []).length ? ` · rests on risk record${p.risk_ids!.length === 1 ? "" : "s"} ${p.risk_ids!.join(", ")}` : ""}</p></li>
          ))}</ol>
        )}
      </Card>
      <Card title={`Extended procedures kept · ${t.kept_extended.length}`}>
        {t.kept_extended.length === 0 ? <p className="text-sm text-muted">No extended procedure was kept.</p> : (
          <ol className="divide-y divide-line text-sm">{t.kept_extended.map((p) => (
            <li key={p.id} className="py-2"><span className="mr-1.5 font-mono text-xs text-muted">{p.task}</span><span className="whitespace-pre-wrap">{p.text}</span> <span className="text-xs text-muted">({p.status})</span></li>
          ))}</ol>
        )}
      </Card>
      {drawer}
    </article>
  );
}

function TailoringActs({ t, open, engagementId }: { t: TailoringReview; open: Parameters<typeof ItemAction>[0]["open"]; engagementId: string }) {
  const acts = (["approve", "revert"] as const).map((d) => t.acts[d]).filter((a): a is NonNullable<typeof a> => !!a?.command);
  if (!acts.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {acts.map((a, n) => (
        <Fragment key={a.label}>
          <Button kind={n === 0 ? "primary" : "secondary"} size="sm" data-command={a.command!}
            onClick={() => open({ command: a.command!, engagementId, fixed: a.payload, title: `${a.label} the agent's tailoring of ${t.workpaper}`,
              settles: settleKey(engagementId, `appr:tail:${t.workpaper}`) })}>{a.label}</Button>
        </Fragment>
      ))}
    </div>
  );
}
